import { useMemo } from "react";

import { Spinner } from "@blueprintjs/core";

import { convertToShareItems } from "@/components/pieces/roleplays/sharing/permissionTransformers";
import { ScenarioAutoShareSettings } from "@/components/pieces/roleplays/sharing/ScenarioAutoShareSettings";
import { Button } from "@/components/ui/button";
import {
  CopyLinkButton,
  ShareAccess,
  ShareList,
} from "@/components/ui/sharing";
import { ResourceType, VisibilityScope } from "@/graphql";
import {
  ResourcePermissionGroup,
  ResourcePermissionProgram,
  ResourcePermissionUser,
  SharePermissionState,
} from "@/types/permissions";

const PROGRAM_DISPLAY_LIMIT = 5;

interface ManagePermissionsViewProps {
  sharedUsers: ResourcePermissionUser[];
  sharedGroups: ResourcePermissionGroup[];
  sharedPrograms: ResourcePermissionProgram[];
  visibilityScope: VisibilityScope;
  showAllPrograms: boolean;
  canShareToWorkspace: boolean;
  loading: boolean;
  availablePermissions: SharePermissionState[];
  onUpdateShareStatus: (index: number, status: SharePermissionState) => void;
  onToggleShowPrograms: () => void;
  onVisibilityScopeChange: (scope: "User" | "Workspace") => void;
  onSave: () => void;
  saveDisabled: boolean;
  saveLoading: boolean;
  workspaceInfoMessage?: string;
  resourceType?: ResourceType;
  autoShareSessions?: boolean;
  onAutoShareChange?: (value: boolean) => void;
}

/**
 * View for managing existing permissions
 */
export function ManagePermissionsView({
  sharedUsers,
  sharedGroups,
  sharedPrograms,
  visibilityScope,
  showAllPrograms,
  canShareToWorkspace,
  loading,
  availablePermissions,
  onUpdateShareStatus,
  onToggleShowPrograms,
  onVisibilityScopeChange,
  onSave,
  saveDisabled,
  saveLoading,
  workspaceInfoMessage = "Workspace admins can see some information about this resource in analytics.",
  resourceType,
  autoShareSessions = false,
  onAutoShareChange,
}: ManagePermissionsViewProps) {
  // Memoize share items transformation
  const shareItems = useMemo(() => {
    return convertToShareItems(
      sharedUsers,
      sharedGroups,
      sharedPrograms,
      availablePermissions,
    );
  }, [sharedUsers, sharedGroups, sharedPrograms, availablePermissions]);

  return (
    <>
      <ShareList
        items={shareItems}
        onUpdateShareStatus={onUpdateShareStatus}
        loading={loading}
        title="People with access"
        showAllThreshold={PROGRAM_DISPLAY_LIMIT}
        onToggleShowAll={onToggleShowPrograms}
        showingAll={showAllPrograms}
      />

      <ShareAccess
        visibilityScope={visibilityScope as "User" | "Workspace"}
        onChange={onVisibilityScopeChange}
        canShareToWorkspace={canShareToWorkspace}
        infoMessage={workspaceInfoMessage}
        className="mt-4"
      />

      {/* Auto-share checkbox for scenarios - only show to users with EDIT permission */}
      {resourceType === ResourceType.Scenario &&
        onAutoShareChange &&
        availablePermissions.includes("EDIT") && (
          <ScenarioAutoShareSettings
            checked={autoShareSessions}
            onChange={onAutoShareChange}
          />
        )}

      <div className="flex justify-between mt-8 items-center gap-1">
        <CopyLinkButton />
        <div className="flex items-center gap-3">
          {!saveDisabled && (
            <div className="text-sm text-gray-500">Pending Changes</div>
          )}
          <Button
            onClick={onSave}
            disabled={saveLoading}
            intent="primary"
            className="px-12"
          >
            {saveLoading && <Spinner size={20} />}
            {saveDisabled ? "Done" : "Save"}
          </Button>
        </div>
      </div>
    </>
  );
}
